import React, { useRef, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Printer, X } from 'lucide-react';

/**
 * Print Preview modal — opens an iframe that mirrors exactly what will come
 * out of the printer.  The iframe clones the source page's stylesheets and
 * content, then applies the page's print body class so the running header
 * and other @media-print rules are rendered.
 */
export default function PrintPreviewDialog({
  open,
  onClose,
  contentSelector,
  bodyClass = 'submission-print-mode',
  title = 'Print Preview',
}) {
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Reset iframe loaded state when dialog closes
  useEffect(() => {
    if (!open) {
      setIframeLoaded(false);
    }
  }, [open]);

  // Handle iframe onLoad event
  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  // Populate iframe when it's loaded
  useEffect(() => {
    if (!open || !iframeLoaded) return;
    
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument;
    if (!doc || !doc.body) return;

    // Clear any existing content first
    doc.head.innerHTML = '<meta charset="utf-8">';
    doc.body.innerHTML = '';

    // Clone all <link rel=stylesheet> and <style> tags from the parent
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((el) => {
      try { 
        const clone = el.cloneNode(true);
        doc.head.appendChild(clone); 
      } catch { /* cross-origin link */ }
    });

    // Override CSS — replays the key @media-print rules in @media all
    const override = doc.createElement('style');
    override.textContent = `
      html, body {
        background: #E2E8F0;
        margin: 0;
        padding: 0;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
      body {
        padding: 24px 0;
        min-height: 100%;
      }
      #pp-paper {
        background: #ffffff;
        width: 8.5in;
        min-height: 11in;
        margin: 0 auto;
        box-shadow: 0 4px 24px rgba(0,0,0,0.12);
        padding: 1.45in 0.6in 0.55in 0.6in;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
      }
      .no-print, nav, aside, header, [data-testid="sidebar"] { display: none !important; }
      .print-show { display: block !important; }
      .careiq-card { box-shadow: none !important; border: none !important; break-inside: avoid; }
      input, textarea, select { border: 1px solid #E2E8F0 !important; background: transparent !important; }
      .print-running-header {
        display: block !important;
        position: absolute !important;
        top: 0; left: 0; right: 0;
        padding: 6px 14px 4px 14px !important;
        color: #0A2342;
        background: #fff;
        border-bottom: 2px solid #0E7490;
        box-sizing: border-box;
        height: 1.25in;
        overflow: hidden;
      }
      body[data-pp-mode="patient"] [role="tablist"] { display: none !important; }
      body[data-pp-mode="patient"] [role="tabpanel"][hidden] { display: block !important; }
      body[data-pp-mode="patient"] .print-tab-heading {
        display: block !important;
        font-size: 13px; font-weight: 700; color: #0A2342;
        margin: 14px 0 4px 0; padding-bottom: 3px;
        border-bottom: 1px solid #0E7490;
        font-family: 'Outfit', sans-serif;
      }
      body[data-pp-mode] .collapsed-section { display: grid !important; }
      body[data-pp-mode] .print-exclude { display: none !important; }
      body[data-pp-mode] [data-testid="print-patient-title"] {
        display: block !important;
        font-size: 22px; font-weight: 700; color: #0A2342;
      }
      /* COMPACT PRINT — mirrors index.css so the iframe preview shows
         the same dense layout users will see on paper. */
      body[data-pp-mode="submission"] [data-testid="patient-info-section"] {
        display: none !important;
      }
      body[data-pp-mode="submission"] [data-testid="submission-edit-page"] > * {
        margin-top: 4px !important;
      }
      body[data-pp-mode="submission"] [data-testid="submission-edit-page"] > *:first-child {
        margin-top: 0 !important;
      }
      body[data-pp-mode="submission"] [data-testid="print-header"] {
        margin-bottom: 6px !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] .careiq-card {
        margin: 0 !important; padding: 0 !important; background: transparent !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] .careiq-card > div {
        padding: 2px 6px !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] label {
        display: inline-block !important;
        margin: 0 6px 0 0 !important;
        font-size: 10px !important;
        font-weight: 600 !important;
        color: #0A2342 !important;
        line-height: 1.2 !important;
        vertical-align: middle;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] .careiq-card > div > div {
        display: inline-block !important;
        background: transparent !important;
        border: 0 !important;
        border-bottom: 1px solid #94A3B8 !important;
        border-radius: 0 !important;
        padding: 1px 4px !important;
        min-height: 18px !important;
        min-width: 60px !important;
        font-size: 10.5px !important;
        line-height: 1.25 !important;
        vertical-align: middle;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] .flex-wrap {
        gap: 4px 8px !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] h3 {
        font-size: 11px !important;
        margin-top: 6px !important;
        padding-bottom: 1px !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] [data-testid^="view-calctable-"],
      body[data-pp-mode] [data-testid="form-filled-view"] [data-testid^="view-signature-"],
      body[data-pp-mode] [data-testid="form-filled-view"] [data-testid^="view-checktable-"] {
        display: block !important;
        border: 1px solid #E2E8F0 !important;
        padding: 4px !important;
        margin-top: 2px !important;
        width: 100% !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] table {
        font-size: 9.5px !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] table th,
      body[data-pp-mode] [data-testid="form-filled-view"] table td {
        padding: 2px 4px !important;
      }
      body[data-pp-mode] [data-testid="form-filled-view"] .careiq-card > div > div[class*="whitespace-pre-wrap"] {
        display: block !important;
        width: 100% !important;
        min-height: 28px !important;
      }
    `;
    doc.head.appendChild(override);

    // Clone source content into the iframe
    const source = document.querySelector(contentSelector);
    const paper = doc.createElement('div');
    paper.id = 'pp-paper';
    if (source) {
      paper.innerHTML = source.outerHTML;
    } else {
      paper.innerHTML = '<p style="padding:40px;text-align:center;color:#64748B">No content to preview.</p>';
    }
    doc.body.appendChild(paper);

    // Apply the page's real print body-class
    doc.body.classList.add(bodyClass);
    const mode = bodyClass.startsWith('patient') ? 'patient' : 'submission';
    doc.body.setAttribute('data-pp-mode', mode);
  }, [open, iframeLoaded, contentSelector, bodyClass]);

  const triggerPrint = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.focus();
    try {
      iframe.contentDocument?.body?.removeAttribute('data-pp-mode');
    } catch { /* ignore */ }
    iframe.contentWindow.print();
    try {
      const bc = iframe.contentDocument?.body;
      if (bc) bc.setAttribute('data-pp-mode', bodyClass.startsWith('patient') ? 'patient' : 'submission');
    } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl p-0 overflow-hidden" data-testid="print-preview-dialog">
        <DialogHeader className="px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC] flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2 text-[#0A2342] text-base">
            <Printer className="w-4 h-4 text-[#0E7490]" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-[#E2E8F0] h-[75vh] overflow-auto" data-testid="print-preview-viewport">
          <iframe
            ref={iframeRef}
            title="Print preview"
            className="w-full h-full border-0"
            data-testid="print-preview-iframe"
            srcDoc="<!DOCTYPE html><html><head><meta charset='utf-8'></head><body></body></html>"
            onLoad={handleIframeLoad}
          />
        </div>
        <DialogFooter className="px-5 py-3 border-t border-[#E2E8F0] bg-white gap-2">
          <Button variant="outline" onClick={onClose} data-testid="print-preview-close-btn">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button className="careiq-btn-primary" onClick={triggerPrint} data-testid="print-preview-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
